<script setup lang="ts">
  import { ref, computed, nextTick, onMounted, watch } from "vue";
  import { IconArrowLeft, Chip, Button, IconPlus, Textarea } from "@ui-kit";
  import { StatusBar, TopBar } from "@src/components";
  import { useOrderCreate, useProfileOptions, type ItemWithSizeList } from "@src/hooks";
  import { useUserInfoStore } from "@src/store";
  import { ResponseCode, orderCreateApi } from "@src/api";
  import { getSizeList } from "./utils";
  import { default as Section } from "./Section.vue";
  import { default as SectionBlock } from "./SectionBlock.vue";
  import { default as SectionRow } from "./SectionRow.vue";
  import { default as OrderType } from "./OrderType.vue";
  import { default as OrderCreateAddProductModal } from "./OrderCreateAddProductModal.vue";
  import { default as OrderCreateItem } from "./OrderCreateItem.vue";
  import { default as OrderCreateSuccess } from "./OrderCreateSuccess.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  const addModalVisible = ref(false);
  const scrollAnchorRef = ref<HTMLElement | null>(null);
  const is_pending_order_create = ref(false);

  const order_create = useOrderCreate();
  const userInfoStore = useUserInfoStore();
  const { hasPermission } = useProfileOptions();
  const has_pickup_commerce_permission = computed(() => hasPermission("mobile.order.pickup.pickup_commerce"));

  watch(
    has_pickup_commerce_permission,
    (has_pickup_commerce_permission) => {
      order_create.setType(has_pickup_commerce_permission ? null : "pickup");
    },
    { immediate: true },
  );

  const handleClickCreateTask = async () => {
    if (!order_create.value.type) return;

    is_pending_order_create.value = true;

    const goods = order_create.value.items.reduce((acc, cur) => {
      const preparedGoods = cur.sizeList
        .filter((size) => size.count > 0)
        .map((size) => ({ item_id: size.item_id, quantity: size.count }));
      acc.push(...preparedGoods);

      return acc;
    }, []);

    try {
      const result = await orderCreateApi({
        goods,
        order_type: order_create.value.type,
        place_from: order_create.value.from.zone,
        place_section: order_create.value.to.section,
        place_to: order_create.value.to.zone,
        ...(order_create.value.from.store_type ? { place_from_type: order_create.value.from.store_type } : {}),
        ...(order_create.value.comment ? { comment: order_create.value.comment } : {}),
      });

      if (result.code === ResponseCode.OK) {
        order_create.setOrderNumber(result.order.order_number);
        order_create.setStage("success");
      }
    } catch (error) {
      console.error(error);
    } finally {
      is_pending_order_create.value = false;
    }
  };

  const handleUpdate = (item: ItemWithSizeList) => {
    order_create.update(item.item_id, item);
  };

  const isButtonCreateDisabled = computed(() => {
    const hasZeroCount = order_create.value.items.some((item) => item.sizeList?.every((size) => size.count === 0));

    return (
      order_create.value.items.length === 0 ||
      hasZeroCount ||
      order_create.value.type === null ||
      order_create.value.from.zone === null ||
      order_create.value.to?.zone === null ||
      order_create.value.to?.section === null
    );
  });

  const handleItemAdded = () => {
    nextTick(() => {
      scrollAnchorRef.value?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const handleRemove = (id: string) => {
    order_create.remove(id);
  };

  const handleClose = () => {
    emit("close");
    order_create.reset();
  };

  onMounted(async () => {
    order_create.setLoading(true);
    // берём первую запись, так как это единственный элемент при открытии
    const item = order_create.value.items[0];

    const sizeList = await getSizeList(item.is_virtual ? item.external_id : item.seria_guid);

    if (sizeList) {
      order_create.update(item.item_id, { ...item, sizeList });
      order_create.setLoading(false);
    }
  });
</script>

<template>
  <div class="order-create-widget" data-testid="order-create-widget">
    <StatusBar />
    <template v-if="order_create.isCreated()">
      <TopBar :title="$t('Создание запроса')" @click:left="handleClose" data-testid="order-create-top-bar">
        <template #left>
          <IconArrowLeft />
        </template>
      </TopBar>
      <div class="content">
        <OrderType
          v-if="has_pickup_commerce_permission"
          :type="order_create.value.type"
          @change="order_create.toggleType($event)"
        />
        <Section :title="$t('Куда')">
          <SectionBlock :title="$t('Секция')">
            <SectionRow data-testid="order-create-section-to-chip">
              <Chip
                label="WOMAN"
                :selected="order_create.value.to?.section === 'woman'"
                @click="order_create.setToSection('woman')"
              />
              <Chip
                label="MAN"
                :selected="order_create.value.to?.section === 'man'"
                @click="order_create.setToSection('man')"
              />
              <Chip
                label="KIDS"
                :selected="order_create.value.to?.section === 'kids'"
                @click="order_create.setToSection('kids')"
              />
            </SectionRow>
          </SectionBlock>
          <SectionBlock :title="$t('Зона')">
            <SectionRow data-testid="order-create-zone-to-chip">
              <Chip
                :label="$t('Зал')"
                :selected="order_create.value.to?.zone === 'shopfloor'"
                @click="order_create.setToZone('shopfloor')"
              />
              <Chip
                :label="$t('Примерочные')"
                :selected="order_create.value.to?.zone === 'fittingroom'"
                @click="order_create.setToZone('fittingroom')"
              />
              <Chip
                :label="$t('Кассы')"
                :selected="order_create.value.to?.zone === 'checkout'"
                @click="order_create.setToZone('checkout')"
              />
            </SectionRow>
          </SectionBlock>
          <Textarea
            v-model="order_create.value.comment"
            name="comment"
            :maxlength="255"
            :label="$t('Комментарий')"
            variant="filled"
          />
        </Section>
        <Section :title="$t('Откуда')">
          <SectionBlock>
            <SectionRow data-testid="order-create-zone-from-chip">
              <Chip
                :label="$t('Зал')"
                :selected="order_create.value.from?.zone === 'shopfloor'"
                @click="order_create.setFromZone('shopfloor')"
              />
              <template v-if="userInfoStore.store_types.length > 0">
                <Chip
                  v-for="store_type in userInfoStore.store_types"
                  :key="store_type"
                  :label="`${$t('Склад')}: ${store_type}`"
                  :selected="order_create.value.from?.store_type === store_type"
                  @click="
                    () => {
                      order_create.setFromStoreType(store_type);
                    }
                  "
                />
              </template>
              <Chip
                v-else
                :label="$t('Склад')"
                :selected="order_create.value.from?.zone === 'store'"
                @click="order_create.setFromZone('store')"
              />
            </SectionRow>
          </SectionBlock>
        </Section>
        <Section :title="`${$t('Сколько')}?`" class="count-items">
          <OrderCreateItem
            v-for="item in order_create.value.items"
            :key="item.item_id"
            :item="item"
            @update="handleUpdate"
            @remove="handleRemove"
          />
          <div ref="scrollAnchorRef" class="scroll-anchor"></div>
        </Section>
        <Button
          class="add-item-button"
          variant="tertiary"
          size="s"
          @click="addModalVisible = true"
          data-testid="order-create-add-product-button"
        >
          <template #iconBefore>
            <IconPlus />
          </template>
          {{ $t("Добавить товар") }}
        </Button>
      </div>
      <div class="footer">
        <Button
          @click="handleClickCreateTask"
          data-testid="order-create-button"
          :disabled="isButtonCreateDisabled"
          :isLoading="is_pending_order_create"
        >
          {{ $t("Создать запрос") }}
        </Button>
      </div>
    </template>
    <template v-if="order_create.isSuccess()">
      <OrderCreateSuccess @close="handleClose" />
    </template>
  </div>
  <OrderCreateAddProductModal
    :visible="addModalVisible"
    @close="addModalVisible = false"
    @item:added="handleItemAdded"
  />
</template>

<style scoped>
  .order-create-widget {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1;
    flex-direction: column;
    background-color: var(--sf-base-background-white);

    .content {
      display: flex;
      gap: 20px;
      flex-direction: column;
      flex: 1;
      padding: 16px;
      overflow-y: auto;

      & :deep(.textarea textarea) {
        min-height: 80px;
        max-height: 80px;
      }

      .count-items {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .scroll-anchor {
          height: 0;
        }
      }
    }

    .add-item-button {
      width: 100%;
      margin-top: auto;
      flex-shrink: 0;
    }

    .footer {
      padding: 16px;
      border-radius: 16px 16px 0 0;
      box-shadow: var(--sf-shadow-top-s);

      & :deep(.button) {
        width: 100%;
      }
    }
  }
</style>
